<script>
import {useStore} from "vuex";
import {ref} from "@vue/reactivity";
import {computed, onBeforeMount, reactive, watch} from "@vue/runtime-core";
import BaseTable from "@/components/Tables/BaseTable.vue";
import TablePagination from "@/components/OtherElements/TablePagination.vue";
import Preloader from "@/components/Technical/Preloader.vue";
import GroupQuotaRow from "@/feature/group-control/GroupQuotaRow.vue";
import ExcelLoadRow from "@/feature/users/ExcelLoadRow.vue";
import GroupTable from "@/feature/group-control/tables/GroupTable.vue";
import {useRoute} from "vue-router";
import useCheckRights from "./../../mixins/useCheckRights";

export default {
  name: "InstrumentsControl",
  components: {GroupTable, ExcelLoadRow, GroupQuotaRow, Preloader, TablePagination, BaseTable},
  setup () {
    const store = useStore(),
        route = useRoute(),
        checkedList = ref([]),
        currentPage = ref(1),
        statusUsersDeactivated = ref(false),
        user = computed(() => store.state.profile.profile),
        {getRight} = useCheckRights(),
        permittedSubscriptions = computed(() => store.state.subscription.permittedSubscriptions),
        availableSubscriptionTools = ref([]);

    const loadPreloader = ref(false);
    const sortType = ref("");

    const staffTableFields = [
      {
        field: "name",
        headerName: "Имя",
        link: true,
        dest: 'staff',
        res: "edit",
      },
      {
        field: "instrumentCheckbox",
        headerName: "Дневник Эмоций",
        type: 'diary',
      },
      {
        field: "instrumentCheckbox",
        type: 'tracker',
        headerName: "Навигатор Эмоций",
      },
    ];
    const countOnPage = ref(15),
        isPreloader = ref(false);

    const users = computed(() => store.state.users.users),
        totalUsers = computed(() => store.state.users.totalUsers),
        rowData = computed(() => {
          return users.value.map((staff) => {
            return {
              id: staff.id,
              name: staff.name,
              edit: staff.id,
            };
          })
        });

    const instrumentsAccess = reactive({
      'diary': [],
      'tracker': []
    });

    onBeforeMount(() => {
      loadPreloader.value = true;
      if (!permittedSubscriptions.value) {
        if (getRight('subscription') && user.value?.company?.id) {
          store.dispatch('subscription/getPermittedSubscriptions', user.value.company?.id);
        }
      } else {
        prepareData();
      }
    });

    const prepareData = () => {
      permittedSubscriptions.value.map((subscription) => {
        if (subscription.instruments.length) {
          subscription.instruments.forEach((instrument) => {
            if (!availableSubscriptionTools.value.includes(instrument)) {
              availableSubscriptionTools.value.push(instrument);
            }
          });
        }

        if (subscription.staff_instruments_access) {
          subscription.staff_instruments_access.diary.forEach((staff) => {
            setInstrumentAccess('diary', staff);
          });
          subscription.staff_instruments_access.tracker.forEach((staff) => {
            setInstrumentAccess('tracker', staff);
          });
        }
      })

      store.dispatch("users/getStaff", {
        perPage: 10,
        page: currentPage.value,
      }).then(() => loadPreloader.value = false);
    }

    const changePage = (page) => {
      if (page !== currentPage.value) {
        currentPage.value = page;
        loadPreloader.value = true;
        usersRequest();
      }
    };

    const usersRequest = () => {
      loadPreloader.value = true;

      const requestData = {
        perPage: 10,
        page: currentPage.value,
      };

      return store
          .dispatch("users/getStaff", requestData)
          .finally(() => (loadPreloader.value = false));
    };

    const diaryUserPermission = ref([]);
    const trackerUserPermission = ref([]);

    const setInstrumentAccess = (type, userId) => {

      if (userId === 'all') {
        if (rowData.value.every((staff) => instrumentsAccess[type].includes(staff.id))) {
          instrumentsAccess[type] = instrumentsAccess[type].filter(
              (currentId) => rowData.value.includes((staff) => staff.id === currentId)
          );
        } else {
          rowData.value.forEach((staff) => instrumentsAccess[type].includes(staff.id) ? null : instrumentsAccess[type].push(staff.id))
        }
      } else {
        if (instrumentsAccess[type].includes(userId)) {
          instrumentsAccess[type] = instrumentsAccess[type].filter(
              (currentId) => currentId !== userId
          );
        } else {
          instrumentsAccess[type].push(userId);
        }
      }
    }

    const updatePermission = (type, userId) => {
      setInstrumentAccess(type, userId);

      let subscriptionId = permittedSubscriptions.value.find((sub) => sub.instruments.includes(type)).id;

      let data = {
        subscription_id: subscriptionId,
        diary: instrumentsAccess['diary'],
        tracker: instrumentsAccess['tracker'],
      }

      store.dispatch('subscription/setInstrumentsAccess', data)
    }

    watch(() => permittedSubscriptions.value, () => {
      prepareData();
    });

    return {
      route,
      countOnPage,
      isPreloader,
      users,
      checkedList,
      totalUsers,
      changePage,
      loadPreloader,
      sortType,
      staffTableFields,
      rowData,
      currentPage,
      statusUsersDeactivated,
      availableSubscriptionTools,
      updatePermission,
      diaryUserPermission,
      trackerUserPermission,
      instrumentsAccess
    };
  },
}
</script>

<template>
  <div class="view-container">
    <div class="card staff-table">
      <div class="card-header card-header-divider">
        <div class="card-title">Доступ к инструментам</div>
      </div>
      <div class="card-body">
        <div v-if="!loadPreloader && rowData.length && availableSubscriptionTools.length">
          <base-table
              :rows=rowData
              :columns="staffTableFields" :checkedList=checkedList
              :availableSubscriptionTools="availableSubscriptionTools"
              :specialCheckboxAction="updatePermission"
              :instrumentsAccess="instrumentsAccess"
          />

          <table-pagination
              :totalPages="15"
              :countOnPage="countOnPage"
              :total="totalUsers"
              :count="users?.length"
              :startPage="currentPage"
              @changePage="changePage"
          />
        </div>
        <div v-if="loadPreloader" class="table-preloader">
          <preloader/>
        </div>
        <div v-if="!loadPreloader && !rowData.length">
          <p class="no_results">
            Нет результатов
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.no_results {
  padding: 40px 10px;
}

.group-filters {
  width: 700px;
  display: flex;
}

.search_filter {
  width: 400px;
  flex-grow: 1;
}

@media (max-width: 1251px) {
  .no_results {
    padding: 40px 10px;
  }
}

@media (max-width: 768px) {
  .search_filter {
    width: 100%;
  }

  .group-filters {
    width: 100%;
    flex-direction: column;
  }
}
</style>